<script lang="ts" setup>
import {
  NSelect,
  type SelectRenderLabel,
  type SelectRenderTag
} from 'naive-ui';
import {ref, watch} from "vue";
import {useAxiosStore} from "@/stores/axios-store";
import type {SelectBaseOption, SelectMixedOption, Value} from 'naive-ui/es/select/src/interface';

type BivariantCallback<TArgs extends unknown[], TResult> = {
  bivarianceHack: (...args: TArgs) => TResult;
}['bivarianceHack'];

interface Props {
  class?: string | string[] | Record<string, boolean> | null | undefined,
  disabled?: boolean,
  value?: unknown | null,
  loading?: boolean,
  page: number,
  pageCount: number,
  fetchFunction: () => Promise<void>,
  fetchAndAppendFunction?: () => Promise<void>,
  options: SelectMixedOption[] | undefined,
  renderLabelFunction?: ((option: any) => any) | SelectRenderLabel,
  renderTagFunction?: ((props: any) => any) | SelectRenderTag,
  updateValueFunction?: BivariantCallback<[unknown], Promise<void> | void>,
  changeFunction?: BivariantCallback<[unknown], Promise<void> | void>,
  placeholder?: string,
  clearable?: boolean,
  size?: "small" | "tiny" | "medium" | "large",
  childrenField?: string,
  showArrow?: boolean,
  consistentMenuWidth?: boolean,
  round?: boolean,
  remote?: boolean,
  multiple?: boolean,
}

const axiosStore = useAxiosStore();

const emits = defineEmits(['update:loading', 'update:search', 'update:page', 'update:value'])

const props = withDefaults(defineProps<Props>(), {
  class: undefined,
  clearable: true,
  size: 'medium',
  childrenField: 'children',
  showArrow: true,
  disabled: false,
  loading: false,
  fetchAndAppendFunction: async () => {},
});

const handleUpdateValue = (value: any) => {
  localValue.value = value as unknown;
  void props.updateValueFunction?.(value as unknown);
  void props.changeFunction?.(value as unknown);
};

const localValue = ref<any>(props.value ?? null);

watch(() => props.value, (newValue) => {
  localValue.value = newValue;
});

watch(localValue, (newValue) => {
  emits('update:value', newValue);
});

let searchTimer: ReturnType<typeof setTimeout>;
let scrollTimer: ReturnType<typeof setTimeout>;

const handleSearch = async (value: string) => {
  emits('update:loading', true);

  axiosStore.abort("Typing")
  clearTimeout(searchTimer);

  searchTimer = setTimeout(async () => {
    emits('update:search', value);
    await updateAfterFilter();
  }, 400);
}

const updateAfterFilter = async () => {
  emits('update:page', 1);
  await props.fetchFunction();
}

const handleScroll = async (e: Event) => {
  if (props.page === props.pageCount) return;

  const currentTarget = e.currentTarget as HTMLElement
  if (
      currentTarget.scrollTop + currentTarget.offsetHeight >=
      currentTarget.scrollHeight
  ) {
    emits('update:loading', true);

    axiosStore.abort("Next page");
    clearTimeout(scrollTimer);

    scrollTimer = setTimeout(async () => {
      const newPage = props.page + 1;
      emits('update:page', newPage);
      await props.fetchAndAppendFunction?.();
    }, 400);
  }
}
</script>

<template>
  <n-select v-model:value="localValue"
            :class="[props.class, { round: props.round }]"
            :options="props.options"
            :placeholder="props.placeholder"
            :clearable="clearable"
            filterable
            :size="size"
            :loading="props.loading"
            :disabled="props.disabled"
            :show-arrow="props.showArrow"
            :consistent-menu-width="props.consistentMenuWidth"
            :children-field="props.childrenField"
            :reset-menu-on-options-change="false"
            :render-label="props.renderLabelFunction"
            :render-tag="props.renderTagFunction"
            :on-search="handleSearch"
            :remote="props.remote"
            :multiple="props.multiple"
            @scroll="handleScroll"
            @update:value="handleUpdateValue">
    <template #empty>
      <slot name="empty"></slot>
    </template>
  </n-select>
</template>

<style lang="scss" scoped>

</style>
